import { groupBy, keyBy, uniq, uniqBy } from 'es-toolkit';
import { AssetType } from '@skynet/types-asset-type';

import type {
  BalancesCorrectionTokenOption,
  JsonBalance,
  JsonBalanceAsset,
} from 'src/store/shared/balances/types';
import type {
  JsonBalanceHistoryAsset,
  JsonBalanceHistoryExtended,
  UniqueTokensList,
} from 'src/store/shared/balance-history/types';

import { getToken } from '../core/helpers';
import { sortObjType, smallTokensThreshold } from '../core/constants';

export const getUniqueCompareTokens = ({
  data = [],
  isIgnoreOther = false,
  tokenMap = {},
}: {
  data?: JsonBalanceHistoryExtended[];
  isIgnoreOther?: boolean;
  tokenMap?: Record<string, string>;
}) => {
  const assetsTokens = data.flatMap((item) => {
    const assets = item.assets.filter((asset) => !!asset.comparison);

    return assets.map((asset) => {
      return {
        isSmallBalance: asset.comparison.isSmallBalance,
        token: getToken(asset.token, tokenMap),
        type: sortObjType[asset.type],
      };
    });
  });

  const groupTokens = groupBy(assetsTokens, (item) => item.token);

  let tokens = Object.entries(groupTokens).map(([key, item]) => {
    return {
      token: key,
      type: Math.min(...item.map((i) => i.type)), // if the same token has different types: e.g. "quote" and "other", then we convert it to "quote"
      isSmallBalance: item.every((i) => i.isSmallBalance),
    };
  });

  if (isIgnoreOther) {
    tokens = tokens.filter((item) => item.type !== 2);
  }

  const sortTokens = Object.values(groupBy(tokens, (item) => item.type)).flat();

  return uniqBy(sortTokens, (item) => item.token);
};

export const getUniqueBalanceTokens = ({
  data = [],
  isIgnoreOtherTokens = false,
  supportedTokens = [],
  tokenMap = {},
}: {
  data?: JsonBalance[];
  isIgnoreOtherTokens?: boolean;
  supportedTokens?: UniqueTokensList[];
  tokenMap?: Record<string, string>;
}) => {
  // Ensure these support token objects have the numeric fields expected by JsonBalanceAsset
  const supportTokensOnBalance: JsonBalanceAsset[] = supportedTokens.map(({ token, type }) => ({
    token: getToken(token, tokenMap),
    type: sortObjType[type],
    // minimal numeric fields to satisfy JsonBalanceAsset shape
    lastPrice: 0,
    volume: 0,
    volumeUSD: 0,
    leverage: 0,
    entryPrice: 0,
    assetType: AssetType.Spot,
    positionType: null,
    unrealizedPnL: 0,
    marginBalance: 0,
  }));

  const objSupportTokens = keyBy(supportedTokens, (item) => item.token);

  let tokens: JsonBalanceAsset[] = data.flatMap((item) => {
    return item.assets.map((asset) => {
      let token = asset.token;
      let type = asset.type;

      if (objSupportTokens[token]) {
        token = objSupportTokens[token].token;
        type = objSupportTokens[token].type;
      }

      return {
        ...asset,
        token: getToken(token, tokenMap),
        type: sortObjType[type],
      } satisfies JsonBalanceAsset;
    });
  });

  tokens = [...tokens, ...supportTokensOnBalance];

  if (isIgnoreOtherTokens) {
    tokens = tokens.filter((item) => item.type !== 2);
  }

  const infoTokens = getTokenInfo(tokens);
  return Object.values(groupBy(infoTokens, (item) => item.type)).flat();
};

export const getUniqueBalanceHistoryTokens = ({
  data = [],
  isSmallBalancesVisible = false,
  tokenMap = {},
}: {
  data?: JsonBalanceHistoryExtended[];
  isSmallBalancesVisible?: boolean;
  tokenMap?: Record<string, string>;
}) => {
  const tokens = data.flatMap((item) => {
    const assets = isSmallBalancesVisible
      ? item.assets.filter((asset) => !!asset.comparison)
      : item.assets.filter((asset) => !!asset.comparison && !asset.comparison?.isSmallBalance);

    return assets.map((asset) => getToken(asset.token, tokenMap));
  });

  return uniq(tokens).sort((a, b) => a.localeCompare(b));
};

export const getTokenOpts = (
  assets: JsonBalanceAsset[] | JsonBalanceHistoryAsset[],
  accountTokens: string[] = [],
): BalancesCorrectionTokenOption[] => {
  // Both JsonBalanceAsset and JsonBalanceHistoryAsset include a `token` field.
  // Cast to a loose shape to avoid lodash union overload issues, then filter undefined.
  const assetsToken = (assets ?? []).map((asset) => asset?.token).filter((t): t is string => !!t);

  return uniq([...assetsToken, ...accountTokens]).map((token: string) => ({
    label: token,
    value: token,
  }));
};

export const getTokenInfo = (list: JsonBalanceAsset[]) => {
  const tokens = groupBy(list, (item) => item.token);
  const tokenList: UniqueTokensList[] = [];
  Object.entries(tokens).forEach(([key, item]) => {
    // Sum the token's USD value across accounts to decide if the whole column
    // is "small". OTHER-category tokens (type 2) must count their value here too:
    // exclusion of OTHER tokens is driven solely by the "ignore other tokens"
    // toggle and is already applied upstream (see getUniqueBalanceTokens). Once a
    // token reaches this point it is part of the selection and is treated like
    // BASE/QUOTE — so e.g. a $85 BNB balance is not hidden by the small-balances
    // toggle.
    const calculateTotal = item.reduce((sum, val) => {
      if (!val?.lastPrice) {
        return sum;
      }

      const total = val.volume * val.lastPrice;
      return sum + total;
    }, 0);

    tokenList.push({
      token: key,
      isSmallBalance: Math.abs(calculateTotal) < smallTokensThreshold,
      type: item[0].type,
    });
  });

  return tokenList;
};
